import { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, ExternalLink, Pencil, Trash2, CheckCircle2, RotateCcw } from 'lucide-react';
import type { WishlistItem } from '../../types';
import {
  getWishlistItems,
  addWishlistItem,
  updateWishlistItem,
  deleteWishlistItem,
  reorderWishlistItems,
} from '../../services/firestore';
import { auth } from '../../lib/firebase';
import { Button } from '../ui/button';
import { formatCurrency } from '../../lib/format';
import { WishlistDialog } from '../dialogs/wishlist-dialog';

function daysUntil(date: Date): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

interface SortableItemProps {
  item: WishlistItem;
  index: number;
  onEdit: (item: WishlistItem) => void;
  onDelete: (item: WishlistItem) => void;
  onComplete: (item: WishlistItem) => void;
}

function SortableItem({ item, index, onEdit, onDelete, onComplete }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const days = daysUntil(item.targetDate);
  const daysLabel =
    days === 0 ? 'Hoje!' : days < 0 ? `${Math.abs(days)}d atrás` : `${days}d`;
  const daysColor =
    days < 0 ? 'text-red-500' : days <= 7 ? 'text-amber-500' : 'text-muted-foreground';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3"
    >
      {/* Priority index */}
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
        {index + 1}
      </span>

      {/* Drag handle */}
      <button
        type="button"
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground truncate">{item.title}</span>
          {item.links?.map((href, i) => (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-muted-foreground hover:text-primary"
              title={href}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="size-3.5" />
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="font-semibold text-foreground">
            R$ {formatCurrency(item.averagePrice)}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className={daysColor}>
            {item.targetDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
            {' '}({daysLabel})
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onComplete(item)}
          title="Marcar como realizado"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-green-50 hover:text-green-600"
        >
          <CheckCircle2 className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onEdit(item)}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(item)}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

interface CompletedItemProps {
  item: WishlistItem;
  onUncomplete: (item: WishlistItem) => void;
  onDelete: (item: WishlistItem) => void;
}

function CompletedItem({ item, onUncomplete, onDelete }: CompletedItemProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
      <CheckCircle2 className="size-4 shrink-0 text-green-600" />

      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground line-through decoration-muted-foreground/50 truncate">
            {item.title}
          </span>
          {item.links?.map((href, i) => (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-muted-foreground hover:text-primary"
              title={href}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="size-3.5" />
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="font-semibold text-muted-foreground">
            R$ {formatCurrency(item.averagePrice)}
          </span>
          {item.completedAt && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">
                realizado em{' '}
                {item.completedAt.toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                })}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onUncomplete(item)}
          title="Voltar para desejos"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <RotateCcw className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(item)}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

export function WishlistTab() {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WishlistItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<WishlistItem | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const activeItems = items
    .filter((i) => !i.completed)
    .sort((a, b) => a.order - b.order);
  const completedItems = [...items.filter((i) => i.completed)].sort(
    (a, b) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0)
  );

  const load = useCallback(async () => {
    try {
      const data = await getWishlistItems();
      setItems(data);
    } catch {
      /* offline — keep empty */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = activeItems.findIndex((i) => i.id === active.id);
    const newIndex = activeItems.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(activeItems, oldIndex, newIndex).map((item, idx) => ({
      ...item,
      order: idx,
    }));
    setItems((prev) => {
      const byId = new Map(reordered.map((i) => [i.id, i]));
      return prev.map((i) => byId.get(i.id) ?? i);
    });

    await reorderWishlistItems(reordered.map(({ id, order }) => ({ id, order })));
  };

  const handleSubmit = async (
    data: Omit<WishlistItem, 'id' | 'createdAt' | 'addedBy' | 'order'>
  ) => {
    const user = auth.currentUser;
    const addedBy = user?.email ?? 'unknown';

    if (editingItem) {
      await updateWishlistItem(editingItem.id, data);
      setItems((prev) =>
        prev.map((i) => (i.id === editingItem.id ? { ...i, ...data } : i))
      );
    } else {
      const order = items.length;
      const created = await addWishlistItem({ ...data, order, createdAt: new Date(), addedBy });
      setItems((prev) => [...prev, created]);
    }

    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    await deleteWishlistItem(itemToDelete.id);

    if (!itemToDelete.completed) {
      const remainingActive = activeItems
        .filter((i) => i.id !== itemToDelete.id)
        .map((i, idx) => ({ ...i, order: idx }));
      setItems((prev) => {
        const byId = new Map(remainingActive.map((i) => [i.id, i]));
        return prev.filter((i) => i.id !== itemToDelete.id).map((i) => byId.get(i.id) ?? i);
      });
      if (remainingActive.length > 0) {
        await reorderWishlistItems(remainingActive.map(({ id, order }) => ({ id, order })));
      }
    } else {
      setItems((prev) => prev.filter((i) => i.id !== itemToDelete.id));
    }
    setItemToDelete(null);
  };

  const handleComplete = async (item: WishlistItem) => {
    const completedAt = new Date();
    await updateWishlistItem(item.id, { completed: true, completedAt });
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, completed: true, completedAt } : i))
    );

    const remainingActive = activeItems
      .filter((i) => i.id !== item.id)
      .map((i, idx) => ({ id: i.id, order: idx }));
    if (remainingActive.length > 0) {
      await reorderWishlistItems(remainingActive);
      setItems((prev) => {
        const byId = new Map(remainingActive.map((r) => [r.id, r.order]));
        return prev.map((i) => (byId.has(i.id) ? { ...i, order: byId.get(i.id)! } : i));
      });
    }
  };

  const handleUncomplete = async (item: WishlistItem) => {
    const order = activeItems.length;
    await updateWishlistItem(item.id, { completed: false, completedAt: null, order });
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id ? { ...i, completed: false, completedAt: undefined, order } : i
      )
    );
  };

  const openAdd = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };

  const openEdit = (item: WishlistItem) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {activeItems.length} {activeItems.length === 1 ? 'item' : 'itens'} · arraste para reordenar
        </p>
        <Button onClick={openAdd}>
          <Plus className="size-4" />
          Novo desejo
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <span className="text-sm text-muted-foreground">Carregando…</span>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 gap-2">
          <p className="text-sm font-medium text-foreground">Nenhum desejo ainda</p>
          <p className="text-xs text-muted-foreground">Adicione itens que vocês querem comprar</p>
        </div>
      ) : (
        <>
          {activeItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-10 gap-1">
              <p className="text-sm font-medium text-foreground">Nenhum desejo pendente</p>
              <p className="text-xs text-muted-foreground">Todos os desejos já foram realizados 🎉</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={activeItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-2">
                  {activeItems.map((item, index) => (
                    <SortableItem
                      key={item.id}
                      item={item}
                      index={index}
                      onEdit={openEdit}
                      onDelete={setItemToDelete}
                      onComplete={handleComplete}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {completedItems.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                Realizados · {completedItems.length}{' '}
                {completedItems.length === 1 ? 'item' : 'itens'}
              </p>
              {completedItems.map((item) => (
                <CompletedItem
                  key={item.id}
                  item={item}
                  onUncomplete={handleUncomplete}
                  onDelete={setItemToDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      <WishlistDialog
        open={dialogOpen}
        editingItem={editingItem}
        onSubmit={handleSubmit}
        onClose={() => { setDialogOpen(false); setEditingItem(null); }}
        itemToDelete={itemToDelete}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteCancel={() => setItemToDelete(null)}
      />
    </div>
  );
}
